import {
  Attachment,
  EmailConfig,
  EmailConfigEntities,
  MobileNotificationConfig,
  ValidationMessage,
  WidgetBase,
} from '@mm/state/model/WidgetModel';
import { activeCompanyUsers } from '@mm/state/selectors/viewSelectors';
import { getUniqId } from '@mm/utils';
import { cloneDeep } from 'lodash';
import { equals } from 'lodash/fp';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import validator from 'validator';
import isEmail from 'validator/lib/isEmail';
import {
  EMAIL_NOTIFICATION_FIELDS,
  PUSH_NOTIFICATION_FIELDS,
} from '../../../editor/constants/fields';
import EmailWidget from '../../../editor/features/build/requestModels/widgets/EmailWidget';
import { getUpdatedTextWithAttribute } from '@mm/state/helpers/attribute-picker-helpers';
import { getUpdatedTextWithEmoji } from '../../components/EmojiPicker/emoji-picker-helpers';
import { EmailWidgetProps } from '../../widgets/EmailWidget/EmailWidget';
import { useCommonWidgetHandlers } from './use-common-widget-handlers';

export const filterOutWidgetErrorsBasedOnKind = (
  kind: 'email' | 'mobile' | undefined,
  message: ValidationMessage,
) => {
  const args = message.rule.arguments as ValidationMessage[];

  if (args?.length) {
    if (kind === 'email') {
      return args.filter(a =>
        EMAIL_NOTIFICATION_FIELDS.includes(a.field.replace('#/config/', '')),
      );
    }
    if (kind === 'mobile') {
      return args.filter(a =>
        PUSH_NOTIFICATION_FIELDS.includes(a.field.replace('#/config/', '')),
      );
    }
  }
  return args;
};

export const extractEmailsFromString = (s: string): string[] =>
  s
    ?.trim()
    .split(',')
    .map(v => v.trim())
    .filter(v => isEmail(v)) || [];

export const useEmailWidgetHandlers = (
  props: EmailWidgetProps,
  autoUpdate = true,
  entity?: EmailConfigEntities,
) => {
  const { widget } = props;

  const commonWidgetHandlers = useCommonWidgetHandlers();
  const config = widget?.config as unknown as EmailConfig;
  const {
    to,
    replyToEmail,
    subject,
    body,
    bodyDd,
    bodyJson,
    ddEditor,
    markAsFollowUp,
    previewText,
    kind,
    cc,
    bcc,
    emailAttachments,
    entities,
  } = config;

  const {
    subject: entitySubject,
    previewText: entityPreviewText,
    body: entityBody,
    cc: entityCc,
    bcc: entityBcc,
    emailAttachments: entityEmailAttachments,
  } = entity || {};

  const [replyValue, setReplyValue] = useState<string>(
    replyToEmail || 'noreply@customers.ai',
  );
  const [toValue, setToValue] = useState<string>(to || '');
  const [subjectValue, setSubjectValue] = useState(
    (entity ? entitySubject : subject) || '',
  );
  const [previewTextValue, setPreviewTextValue] = useState(
    (entity ? entityPreviewText : previewText) || '',
  );
  const [bodyValue, setBodyValue] = useState(
    (entity ? entityBody : body) || '',
  );

  const [bodyJsonValue, setBodyJsonValue] = useState(bodyJson);
  const [bodyDdValue, setBodyDdValue] = useState(bodyDd);
  const [ddEditorValue, setDdEditorValue] = useState(ddEditor);

  const [ccInputValue, setCcInputValue] = useState('');
  const [bccInputValue, setBccInputValue] = useState('');
  const [ccEmails, setCCEmails] = useState<string[]>(
    extractEmailsFromString(entity ? entityCc || '' : cc),
  );
  const [bccEmails, setBCCEmails] = useState<string[]>(
    extractEmailsFromString(entity ? entityBcc || '' : bcc),
  );

  const [attachments, setAttachments] = useState<Attachment[]>(
    ((entity ? entityEmailAttachments : emailAttachments) || []).map(a => ({
      uniqId: getUniqId('email-attachment'),
      id: a.id,
      name: a.originalFileName,
    })),
  );

  const [configEntities, setConfigEntities] = useState<EmailConfigEntities[]>(
    entities || [],
  );

  const [isReplyEmailValid, setIsReplyEmailValid] = useState(true);
  const [isToEmailValid, setIsToEmailValid] = useState(true);
  const [markAsFollowUpValue, setMarkAsFollowUpValue] = useState(
    markAsFollowUp || false,
  );
  const emailBodyRef = useRef<HTMLTextAreaElement | null>(null);
  const setEmailBodyRef = (ref: HTMLTextAreaElement) => {
    emailBodyRef.current = ref;
  };
  const emailSubjectRef = useRef<HTMLTextAreaElement | null>(null);
  const setEmailSubjectRef = (ref: HTMLTextAreaElement) => {
    emailSubjectRef.current = ref;
  };
  const emailPreviewTextRef = useRef<HTMLTextAreaElement | null>(null);
  const setEmailPreviewTextRef = (ref: HTMLTextAreaElement) => {
    emailPreviewTextRef.current = ref;
  };

  const validateEmails = () => {
    const validatorUpdated = validator;
    const isToValid = toValue
      .split(',')
      .map(email => validatorUpdated.isEmail(email.trim()))
      .every(email => email);
    const isReplyValid = replyValue
      .split(',')
      .map(email => validatorUpdated.isEmail(email.trim()))
      .every(email => email);

    setIsToEmailValid(isToValid);
    setIsReplyEmailValid(isReplyValid);
    return isToValid && isReplyValid;
  };

  const onChangeTo = ({
    target: { value },
  }: ChangeEvent<HTMLTextAreaElement>) => setToValue(value);

  const onChangeReply = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => setReplyValue(value);

  const onChangeSubject = ({
    target: { value },
  }: ChangeEvent<HTMLTextAreaElement>) => setSubjectValue(value);
  const onChangePreviewText = ({
    target: { value },
  }: ChangeEvent<HTMLTextAreaElement>) => setPreviewTextValue(value);

  const onChangeBody = ({
    target: { value },
  }: ChangeEvent<HTMLTextAreaElement>) => setBodyValue(value);

  const onBlur = () => {
    const updatedKind =
      kind === 'email' || kind === 'to_contact' ? kind : 'email';
    if (validateEmails() || kind === 'to_contact') {
      const updatedWidget = {
        ...cloneDeep(widget),
        config: {
          to: toValue,
          reply_to_email: replyValue,
          subject: subjectValue,
          previewText: previewTextValue,
          body: bodyValue,
          kind: updatedKind,
          entities: configEntities,
        },
      };
      commonWidgetHandlers.update(updatedWidget as any);
    }
  };

  const cloneEntityToMaster = (entity: EmailConfigEntities) => {
    setSubjectValue(entity?.subject);
    setPreviewTextValue(entity?.previewText);
    setBodyValue(entity?.body);
  };

  const updateInsertEmojiBody = (emoji: string) => {
    if (emailBodyRef.current) {
      const newText = getUpdatedTextWithEmoji(
        bodyValue,
        emoji,
        emailBodyRef.current,
      );

      setBodyValue(newText);

      if (autoUpdate) {
        commonWidgetHandlers.updateField(widget, 'config.body', newText);
      }
    }
  };

  const updateInsertAttributeBody = (attribute: string) => {
    if (emailBodyRef.current) {
      const newText = getUpdatedTextWithAttribute(
        bodyValue,
        attribute,
        emailBodyRef.current,
      );

      setBodyValue(newText);
      if (autoUpdate) {
        commonWidgetHandlers.updateField(widget, 'config.body', newText);
      }
    }
  };

  const updateBody = (html: string) => {
    commonWidgetHandlers.updateField(widget, 'config.body', html);
  };

  const updateInsertAttributeSubject = (attribute: string) => {
    if (emailSubjectRef.current) {
      const newText = getUpdatedTextWithAttribute(
        subjectValue,
        attribute,
        emailSubjectRef.current,
      );

      setSubjectValue(newText);

      if (autoUpdate) {
        commonWidgetHandlers.updateField(widget, 'config.subject', newText);
      }
    }
  };

  const updateSubject = (text: string) =>
    commonWidgetHandlers.updateField(widget, 'config.subject', text);

  const updateInsertEmojiSubject = (emoji: string) => {
    if (emailSubjectRef.current) {
      const newText = getUpdatedTextWithEmoji(
        subjectValue,
        emoji,
        emailSubjectRef.current,
      );

      setSubjectValue(newText);

      if (autoUpdate) {
        commonWidgetHandlers.updateField(widget, 'config.subject', newText);
      }
    }
  };

  const updateInsertAttributePreviewText = (attribute: string) => {
    if (emailPreviewTextRef.current) {
      const newText = getUpdatedTextWithAttribute(
        previewTextValue,
        attribute,
        emailPreviewTextRef.current,
      );

      setPreviewTextValue(newText);
      if (autoUpdate) {
        commonWidgetHandlers.updateField(widget, 'config.previewText', newText);
      }
    }
  };

  const updatePreviewText = (text: string) =>
    commonWidgetHandlers.updateField(widget, 'config.previewText', text);

  const updateInsertEmojiPreviewText = (emoji: string) => {
    if (emailPreviewTextRef.current) {
      const newText = getUpdatedTextWithEmoji(
        previewTextValue,
        emoji,
        emailPreviewTextRef.current,
      );

      setPreviewTextValue(newText);

      if (autoUpdate) {
        commonWidgetHandlers.updateField(widget, 'config.previewText', newText);
      }
    }
  };

  const updateMarkAsFollowUp = (newMarkAsFollowUp: boolean) => {
    if (newMarkAsFollowUp !== markAsFollowUp) {
      setMarkAsFollowUpValue(newMarkAsFollowUp);
      commonWidgetHandlers.updateField(
        widget,
        'config.markAsFollowUp',
        newMarkAsFollowUp,
      );
    }
  };

  return {
    toValue,
    replyValue,
    onChangeReply,
    subjectValue,
    previewTextValue,
    bodyValue,
    onChangeTo,
    onChangeSubject,
    onChangePreviewText,
    onChangeBody,
    onBlur,
    updateInsertEmojiBody,
    setEmailBodyRef,
    setEmailSubjectRef,
    setEmailPreviewTextRef,
    updateInsertEmojiSubject,
    updateInsertEmojiPreviewText,
    updateInsertAttributeBody,
    isReplyEmailValid,
    isToEmailValid,
    updateInsertAttributeSubject,
    updateInsertAttributePreviewText,
    markAsFollowUpValue,
    updateMarkAsFollowUp,
    updateBody,
    updateSubject,
    updatePreviewText,
    ccInputValue,
    setCcInputValue,
    setBccInputValue,
    bccInputValue,
    ccEmails,
    setCCEmails,
    bccEmails,
    setBCCEmails,
    attachments,
    setAttachments,
    configEntities,
    setConfigEntities,
    cloneEntityToMaster,
    setBodyValue,
    setSubjectValue,
    bodyJsonValue,
    setBodyJsonValue,
    bodyDdValue,
    setBodyDdValue,
    ddEditorValue,
    setDdEditorValue,
  };
};

export const usePushNotificationWidgetHanlders = () => {
  const commonWidgetHandlers = useCommonWidgetHandlers();

  const addNew = async (
    dialogueId: number,
    sortOrder: number,
    recipient: string,
  ) => {
    const newWidget = EmailWidget.make(dialogueId, sortOrder, recipient);

    return commonWidgetHandlers.create(newWidget, dialogueId, sortOrder);
  };

  return { addNew };
};

export const useMobileNotificationWidgetHandlers = (
  props: EmailWidgetProps,
) => {
  const { widget } = props;

  const commonWidgetHandlers = useCommonWidgetHandlers();

  const {
    config: { message, companyUserIdsToNotify, markAsFollowUp },
  } = widget as unknown as WidgetBase<MobileNotificationConfig>;

  const [messageValue, setMessageValue] = useState(message || 'New lead');
  const [markAsFollowUpValue, setMarkAsFollowUpValue] = useState(
    markAsFollowUp || false,
  );

  const companyUsers = useSelector(activeCompanyUsers);

  const getRemappedCompanyUsers = () =>
    companyUsers.map((usr, idx) => ({
      id: usr.id,
      name: `${usr.firstName} ${usr.lastName}`,
      key: `${idx}`,
    }));

  const getKeys = (
    newUsers?: ReturnType<typeof getRemappedCompanyUsers>,
  ): string[] =>
    (newUsers || users)
      .map((_, idx) => `${idx}`)
      .filter(idx => companyUserIdsToNotify?.includes(users[Number(idx)].id));

  const [users, setUsers] = useState(getRemappedCompanyUsers());
  const [selectedKeys, setSelectedKeys] = useState(getKeys());
  const [isMessageValid, setIsMessageValid] = useState(true);
  const [isUsersToNotifyArrayValid, setIsUsersToNotifyArrayValid] =
    useState(true);

  useEffect(
    () => {
      const newUsers = getRemappedCompanyUsers();
      setUsers(newUsers);
      setSelectedKeys(getKeys(newUsers));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyUsers, companyUserIdsToNotify],
  );

  const validateMessage = (message: string) => {
    const messageValid = !!message.length;
    setIsMessageValid(messageValid);
    return messageValid;
  };

  const validateUsers = (usersToValidate: string[]) => {
    const usersValid = !!usersToValidate?.length;
    setIsUsersToNotifyArrayValid(usersValid);
    return usersValid;
  };

  const validateMobileNotificationsWidget = () => {
    const messageValid = validateMessage(messageValue);
    const usersValid = validateUsers(selectedKeys);
    return messageValid && usersValid;
  };

  const updateMessage = (newText: string) => {
    if (newText !== messageValue) {
      setMessageValue(newText);
      validateMessage(newText);
      commonWidgetHandlers.updateField(widget, 'config.message', newText);
    }
  };

  const updateCompanyUsersToNotify = (keys: string[]) => {
    if (!equals(keys, selectedKeys)) {
      setSelectedKeys(keys);
      validateUsers(keys);
      const updatedUserIds = users
        .filter((_, idx) => (keys ? keys : selectedKeys).includes(`${idx}`))
        .map(u => u.id);

      commonWidgetHandlers.updateField(
        widget,
        'config.companyUserIdsToNotify',
        updatedUserIds,
      );
    }
  };

  const updateMarkAsFollowUp = (newMarkAsFollowUp: boolean) => {
    if (newMarkAsFollowUp !== markAsFollowUp) {
      setMarkAsFollowUpValue(newMarkAsFollowUp);
      commonWidgetHandlers.updateField(
        widget,
        'config.markAsFollowUp',
        newMarkAsFollowUp,
      );
    }
  };

  return {
    users,
    messageValue,
    isMessageValid,
    updateMessage,
    selectedKeys,
    updateCompanyUsersToNotify,
    isUsersToNotifyArrayValid,
    markAsFollowUpValue,
    updateMarkAsFollowUp,
  };
};
